// Состояние Конструктора (README §13): на время сессии страницы — режим,
// камера k/tx/ty, выделение, открытые шаблоны; с сервера — схемы, узлы, связи.
// Меню и полотно — разные компоненты (Sidebar и ленивый View), поэтому стор общий.

import { allowedEdge, layeredLayout } from './geom'
import { create } from 'zustand'
import { api } from '../../api'
import { useAppStore } from '../../store/appStore'
import { flowApi, type SayEvent } from './api'
import type {
  CompilePlan, FlowNode, Mode, NodeType, Scheme, SchemeSummary, SchemeUsage, SuggestionsResponse, Template,
} from './types'

export interface LibraryPick { scheme: string; node: string; type: NodeType | 'skill'; q: string }

interface Rec { target: string; t0: number }

export interface FlowState {
  schemes: SchemeSummary[]
  schemeId: string | null
  scheme: Scheme | null
  loading: boolean
  error: string | null

  mode: Mode
  k: number
  tx: number
  ty: number
  sel: string | null
  selEdge: string | null
  tplOpen: string | null
  /** измеренные высоты карточек (мировые px) — для концов связей и «вписать» */
  heights: Record<string, number>
  /** какие карточки сейчас ждут ответа агента */
  busy: Record<string, boolean>
  plan: CompilePlan | null
  planBusy: boolean
  runBusy: boolean
  notice: string | null
  /** режим «расход»: расход по элементам схемы (GET /api/flow/{id}/usage) */
  usage: SchemeUsage | null
  /** Улучшения (§4.5) выбранной схемы */
  sugg: SuggestionsResponse | null
  /** «перепроверяю…» после ручной правки / «перестраиваю…» после ✓ */
  suggBusy: 'recheck' | 'adapt' | null
  /** рекомендация под курсором — подсветка элемента на полотне */
  suggHover: string | null
  /** раскрытые подробности рекомендации */
  suggRead: string | null
  /** id рекомендаций, которые оператор уже видел (лампочка — непрочитанные) */
  suggSeen: Record<string, true>
  /** согласованные элементы: nodeId → время (карточка обводится --ok) */
  adapted: Record<string, number>
  /** центрировать узел (flowFocus Кабинета, наведение на рекомендацию) */
  focusRequest: { id: string; n: number; always: boolean } | null
  recording: Rec | null
  dictating: 'listen' | 'build' | null
  /** узел, на котором сейчас шаг прогона ([flow] node=… в логе беседы) */
  runStep: string | null
  runPrev: string | null
  /** «открыть библиотеку →»: раздел library читает и заполняет карточку */
  libraryPick: LibraryPick | null
  fitRequest: number

  loadSchemes(): Promise<void>
  openScheme(id: string): Promise<void>
  refresh(): Promise<void>
  createScheme(title?: string, doImport?: boolean): Promise<void>
  renameScheme(id: string, title: string): Promise<void>
  deleteScheme(id: string): Promise<void>
  dismissImport(): Promise<void>

  setMode(m: Mode): void
  setCamera(c: { k?: number; tx?: number; ty?: number }): void
  select(id: string | null): void
  selectEdge(id: string | null): void
  setTplOpen(id: string | null): void
  setHeight(id: string, h: number): void
  requestFit(): void

  patchLocal(id: string, patch: Partial<FlowNode>): void
  saveNode(id: string, patch: Partial<FlowNode> & { stack?: null }): Promise<void>
  saveTextSoon(id: string): void
  saveLayoutSoon(ids: string[]): void
  moveStack(id: string, x: number, y: number): void
  addNode(type: NodeType, x: number, y: number): Promise<void>
  removeNode(id: string): Promise<void>
  addEdge(from: string, to: string): Promise<void>
  removeEdge(id: string): Promise<void>
  unstack(id: string, x: number, y: number): Promise<void>
  say(id: string, text: string, answer?: boolean): Promise<void>
  applyTemplate(id: string, t: Template): Promise<void>
  openLibrary(id: string, q?: string, equipment?: boolean): void
  arrangeLayers(): void
  fillFromLibrary(t: { name: string; title?: string; description?: string; source?: string }): Promise<void>

  compile(): Promise<CompilePlan | null>
  closePlan(): void
  apply(overwrite?: boolean): Promise<void>
  run(overwrite?: boolean): Promise<void>
  loadUsage(): Promise<void>
  loadSuggestions(recheck?: boolean): Promise<void>
  acceptSuggestion(id: string): Promise<void>
  rejectSuggestion(id: string): Promise<void>
  setSuggHover(id: string | null): void
  setSuggRead(id: string | null): void
  markSuggSeen(): void
  focusNode(id: string, always?: boolean): void
  /** Кабинет → «кто тратит»: режим «расход», выделить и центрировать узел агента */
  focusAgent(agentId: string): Promise<void>
  setRecording(r: Rec | null): void
  dictate(text: string): Promise<void>
  resolveProposals(accept: boolean): Promise<void>
  setNotice(t: string | null): void
}

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

const textTimers = new Map<string, number>()
const wait = (ms: number) => new Promise<void>((r) => window.setTimeout(r, Math.max(0, ms)))
/** Согласование само меняет схему — «перепроверяю…» после него не нужен. */
let skipRecheck = false
let layoutTimer = 0
const layoutIds = new Set<string>()
const stackMoves = new Map<string, { x: number; y: number }>()
let runPoll = 0

export const useFlowStore = create<FlowState>()((set, get) => {
  const upd = (fn: (s: Scheme) => Scheme) => {
    const cur = get().scheme
    if (cur) set({ scheme: fn(cur) })
  }
  const replaceNode = (node: FlowNode) =>
    upd((s) => ({ ...s, nodes: s.nodes.map((n) => (n.id === node.id ? { ...n, ...node } : n)) }))
  const fail = (e: unknown) => set({ notice: errText(e) })

  const flushLayout = () => {
    const s = get().scheme
    if (!s) return
    const nodes = [...layoutIds]
      .map((id) => s.nodes.find((n) => n.id === id))
      .filter(Boolean)
      .map((n) => ({ id: n!.id, x: Math.round(n!.x), y: Math.round(n!.y), w: n!.w, h: n!.h }))
    const stacks = [...stackMoves].map(([id, p]) => ({ id, x: Math.round(p.x), y: Math.round(p.y) }))
    layoutIds.clear()
    stackMoves.clear()
    if (nodes.length || stacks.length) flowApi.layout(s.id, { nodes, stacks }).catch(fail)
  }

  return {
    schemes: [],
    schemeId: null,
    scheme: null,
    loading: false,
    error: null,
    mode: 'structure',
    k: 0.8,
    tx: 40,
    ty: 40,
    sel: null,
    selEdge: null,
    tplOpen: null,
    heights: {},
    busy: {},
    plan: null,
    planBusy: false,
    runBusy: false,
    notice: null,
    usage: null,
    sugg: null,
    suggBusy: null,
    suggHover: null,
    suggRead: null,
    suggSeen: {},
    adapted: {},
    focusRequest: null,
    recording: null,
    dictating: null,
    runStep: null,
    runPrev: null,
    libraryPick: null,
    fitRequest: 0,

    async loadSchemes() {
      set({ loading: true, error: null })
      try {
        const { schemes } = await flowApi.list()
        set({ schemes, loading: false })
        const cur = get().schemeId
        const pick = schemes.find((x) => x.id === cur) ?? schemes[0]
        if (pick && (!get().scheme || get().scheme!.id !== pick.id)) await get().openScheme(pick.id)
      } catch (e) {
        set({ loading: false, error: errText(e) })
      }
    },
    async openScheme(id) {
      const other = get().schemeId !== id
      set({ schemeId: id, sel: null, selEdge: null, tplOpen: null, plan: null, ...(other ? { sugg: null, usage: null, suggHover: null, suggRead: null } : {}) })
      try {
        const scheme = await flowApi.get(id)
        if (get().schemeId !== id) return
        set({ scheme, error: null, fitRequest: get().fitRequest + 1 })
        void get().loadSuggestions()
        if (get().mode === 'live') void get().loadUsage()
      } catch (e) {
        set({ error: errText(e) })
      }
    },
    async refresh() {
      const id = get().schemeId
      if (!id) return
      try {
        const scheme = await flowApi.get(id)
        if (get().schemeId === id) set({ scheme })
      } catch (e) { fail(e) }
    },
    async createScheme(title = 'новая схема', doImport = false) {
      try {
        const scheme = await flowApi.create(title, doImport)
        const { schemes } = await flowApi.list()
        set({ schemes, schemeId: scheme.id, scheme, sel: null, fitRequest: get().fitRequest + 1 })
      } catch (e) { fail(e) }
    },
    async renameScheme(id, title) {
      const t = title.trim()
      if (!t) return
      set({ schemes: get().schemes.map((x) => (x.id === id ? { ...x, title: t } : x)) })
      if (get().scheme?.id === id) upd((s) => ({ ...s, title: t }))
      try { await flowApi.patch(id, { title: t }) } catch (e) { fail(e) }
    },
    async deleteScheme(id) {
      try {
        await flowApi.remove(id)
        const { schemes } = await flowApi.list()
        set({ schemes, scheme: get().scheme?.id === id ? null : get().scheme, schemeId: get().schemeId === id ? null : get().schemeId })
        if (!get().schemeId && schemes[0]) await get().openScheme(schemes[0].id)
      } catch (e) { fail(e) }
    },
    async dismissImport() {
      const s = get().scheme
      if (!s?.imported) return
      upd((x) => ({ ...x, imported: x.imported ? { ...x.imported, dismissed: true } : x.imported }))
      try { await flowApi.patch(s.id, { importDismissed: true }) } catch (e) { fail(e) }
    },

    setMode: (mode) => {
      set({ mode, tplOpen: null })
      if (mode === 'live') void get().loadUsage()
    },
    setCamera: (c) => set(c),
    select: (sel) => set((s) => ({ sel, selEdge: null, tplOpen: s.tplOpen === sel ? s.tplOpen : null })),
    selectEdge: (selEdge) => set({ selEdge, sel: null, tplOpen: null }),
    setTplOpen: (tplOpen) => set({ tplOpen }),
    setHeight: (id, h) => {
      if (Math.abs((get().heights[id] ?? 0) - h) < 0.5) return
      set({ heights: { ...get().heights, [id]: h } })
    },
    requestFit: () => set({ fitRequest: get().fitRequest + 1 }),

    patchLocal: (id, patch) => upd((s) => ({ ...s, nodes: s.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) })),
    async saveNode(id, patch) {
      const s = get().scheme
      if (!s) return
      get().patchLocal(id, patch as Partial<FlowNode>)
      try {
        const node = await flowApi.patchNode(s.id, id, patch)
        if (get().scheme?.id === s.id) {
          replaceNode(node)
          if ('engine' in patch || 'skills' in patch) await get().refresh()
        }
      } catch (e) { fail(e) }
    },
    saveTextSoon(id) {
      window.clearTimeout(textTimers.get(id))
      textTimers.set(id, window.setTimeout(() => {
        textTimers.delete(id)
        const n = get().scheme?.nodes.find((x) => x.id === id)
        const s = get().scheme
        if (n && s) flowApi.patchNode(s.id, id, { title: n.title, text: n.text }).catch(fail)
      }, 700))
    },
    saveLayoutSoon(ids) {
      ids.forEach((id) => layoutIds.add(id))
      window.clearTimeout(layoutTimer)
      layoutTimer = window.setTimeout(flushLayout, 400)
    },
    moveStack(id, x, y) {
      upd((s) => ({ ...s, stacks: s.stacks.map((st) => (st.id === id ? { ...st, x, y } : st)) }))
      stackMoves.set(id, { x, y })
      window.clearTimeout(layoutTimer)
      layoutTimer = window.setTimeout(flushLayout, 400)
    },
    async addNode(type, x, y) {
      const s = get().scheme
      if (!s) return
      try {
        const node = await flowApi.addNode(s.id, { type, x: Math.round(x), y: Math.round(y) })
        upd((sc) => ({ ...sc, nodes: [...sc.nodes, node] }))
        set({ sel: node.id, selEdge: null, tplOpen: null })
        get().focusNode(node.id, true)
      } catch (e) { fail(e) }
    },
    async removeNode(id) {
      const s = get().scheme
      if (!s) return
      upd((sc) => ({ ...sc, nodes: sc.nodes.filter((n) => n.id !== id), edges: sc.edges.filter((e) => e.from !== id && e.to !== id) }))
      set({ sel: null, tplOpen: null })
      try { await flowApi.removeNode(s.id, id) } catch (e) { fail(e); void get().refresh() }
    },
    arrangeLayers() {
      const cur = get().scheme
      if (!cur) return
      const nodes = layeredLayout(cur, get().heights)
      set({ scheme: { ...cur, nodes }, fitRequest: get().fitRequest + 1 })
      get().saveLayoutSoon(nodes.map((n) => n.id))
    },
    async addEdge(from, to) {
      const s = get().scheme
      if (!s || !allowedEdge(s.nodes.find((n) => n.id === from), s.nodes.find((n) => n.id === to))) {
        set({ notice: 'разрешены только оркестратор → субагент и субагент → источник' }); return
      }
      if (s.edges.some((e) => e.from === from && e.to === to)) return
      try {
        const edge = await flowApi.addEdge(s.id, { from, to })
        upd((sc) => ({ ...sc, edges: [...sc.edges, edge] }))
        void get().refresh()
      } catch (e) { fail(e) }
    },
    async removeEdge(id) {
      const s = get().scheme
      if (!s) return
      upd((sc) => ({ ...sc, edges: sc.edges.filter((e) => e.id !== id) }))
      set({ selEdge: null })
      try { await flowApi.removeEdge(s.id, id); void get().refresh() } catch (e) { fail(e) }
    },
    async unstack(id, x, y) {
      get().patchLocal(id, { stack: null, x, y })
      set({ sel: id })
      const s = get().scheme
      if (!s) return
      try {
        const node = await flowApi.patchNode(s.id, id, { stack: null, x: Math.round(x), y: Math.round(y) })
        replaceNode(node)
      } catch (e) { fail(e) }
    },

    async say(id, text, answer = false) {
      const s = get().scheme
      if (!s || get().busy[id]) return
      window.clearTimeout(textTimers.get(id))
      set({ busy: { ...get().busy, [id]: true } })
      const onEvent = (e: SayEvent) => {
        const cur = get().scheme?.nodes.find((n) => n.id === id)
        if (!cur) return
        if (e.type === 'line') get().patchLocal(id, { log: [...cur.log, e.line].slice(-50) })
        else if (e.type === 'question') get().patchLocal(id, { question: e.question, status: 'asking' })
        else if (e.type === 'node') replaceNode(e.node)
        // 'error' сервер уже записал строкой «!» — второй раз не показываем
      }
      try {
        await flowApi.say(s.id, id, text, answer, onEvent)
      } catch (e) {
        const cur = get().scheme?.nodes.find((n) => n.id === id)
        if (cur) get().patchLocal(id, { log: [...cur.log, { kind: 'warn', text: `нет связи с агентом: ${errText(e)}`, ts: Date.now() / 1000 }] })
      } finally {
        const b = { ...get().busy }
        delete b[id]
        set({ busy: b })
        void get().refresh()
      }
    },
    async applyTemplate(id, t) {
      set({ tplOpen: null })
      const n = get().scheme?.nodes.find((x) => x.id === id)
      if (!n) return
      const src = t.source === 'library' ? `из библиотеки · ${t.path ?? t.name}` : (t.path ?? 'на сервере')
      await get().saveNode(id, {
        title: t.title || t.name,
        text: t.description || n.text,
        source: src,
        status: 'ready',
      })
      const cur = get().scheme?.nodes.find((x) => x.id === id)
      if (cur) get().patchLocal(id, { log: [...cur.log, { kind: 'ok', text: `из шаблона: ${t.title || t.name}`, ts: Date.now() / 1000 }] })
    },
    openLibrary(id, q = '', equipment = false) {
      const s = get().scheme
      const n = s?.nodes.find((x) => x.id === id)
      if (!s || !n) return
      set({ libraryPick: { scheme: s.id, node: id, type: equipment ? 'skill' : n.type, q }, tplOpen: null })
      useAppStore.getState().setSection('library')
    },
    async fillFromLibrary(t) {
      const pick = get().libraryPick
      if (!pick) return
      set({ libraryPick: null })
      if (get().schemeId !== pick.scheme) await get().openScheme(pick.scheme)
      const owner = get().scheme?.nodes.find((n) => n.id === pick.node)
      if (pick.type === 'skill' && owner && ['orchestrator', 'subagent'].includes(owner.type)) {
        await get().saveNode(owner.id, { skills: [...new Set([...(owner.skills ?? []), t.name])] })
      } else await get().applyTemplate(pick.node, { name: t.name, title: t.title, description: t.description, source: 'library', path: t.source ?? null })
      set({ sel: pick.node })
      useAppStore.getState().setSection('flow')
    },

    async compile() {
      const s = get().scheme
      if (!s) return null
      set({ planBusy: true, tplOpen: null })
      try {
        const plan = await flowApi.compile(s.id)
        set({ plan, planBusy: false })
        return plan
      } catch (e) {
        set({ planBusy: false })
        fail(e)
        return null
      }
    },
    closePlan: () => set({ plan: null }),
    async apply(overwrite = false) {
      const s = get().scheme
      if (!s) return
      set({ planBusy: true })
      try {
        const r = await flowApi.apply(s.id, overwrite)
        set({ plan: null, planBusy: false, notice: `скомпилировано · ${r.written.length} файлов записано` })
        void get().refresh()
      } catch (e) {
        set({ planBusy: false })
        const body = (e as { body?: unknown }).body
        if (body && typeof body === 'object' && 'artifacts' in (body as object)) set({ plan: body as CompilePlan })
        else fail(e)
      }
    },
    async run(overwrite = false) {
      const s = get().scheme
      if (!s || get().runBusy) return
      if (!get().plan) {
        const plan = await get().compile()
        if (!plan) return
        const conflict = plan.artifacts.some((a) => a.action === 'conflict')
        if (!plan.ok || conflict) return // план открыт — там ошибки или конфликты
        set({ plan: null })
      }
      set({ runBusy: true, planBusy: true })
      try {
        const r = await flowApi.run(s.id, overwrite)
        set({ runBusy: false, planBusy: false, plan: null, notice: 'беседа-оркестратор запущена' })
        watchRun(r.sessionId)
        useAppStore.getState().focusSession(r.sessionId)
      } catch (e) {
        set({ runBusy: false, planBusy: false })
        const body = (e as { body?: unknown }).body
        if (body && typeof body === 'object' && 'artifacts' in (body as object)) set({ plan: body as CompilePlan })
        else fail(e)
      }
    },
    async loadUsage() {
      const id = get().schemeId
      if (!id) return
      try {
        const usage = await flowApi.schemeUsage(id)
        if (get().schemeId === id) set({ usage })
      } catch {
        // нет логов/эндпоинта — все элементы «не тратят»
        if (get().schemeId === id) set({ usage: { nodes: {}, rows: {}, totalTokensPerMin: 0, adapted: {}, forecast: null } })
      }
    },
    async loadSuggestions(recheck = false) {
      const id = get().schemeId
      if (!id) return
      const t0 = Date.now()
      if (recheck && !get().suggBusy) set({ suggBusy: 'recheck' })
      try {
        const sugg = await flowApi.suggestions(id)
        // «перепроверяю…» видно хотя бы секунду (README §4.5)
        if (recheck) await wait(1000 - (Date.now() - t0))
        if (get().schemeId === id) set({ sugg })
      } catch {
        /* рекомендации необязательны — блок покажет прежние */
      } finally {
        if (get().suggBusy === 'recheck') set({ suggBusy: null })
      }
    },
    async acceptSuggestion(sug) {
      const id = get().schemeId
      if (!id || get().suggBusy === 'adapt') return
      const item = get().sugg?.items.find((x) => x.id === sug)
      set({ suggBusy: 'adapt', suggHover: null })
      const t0 = Date.now()
      try {
        const r = await flowApi.accept(id, sug)
        await wait(900 - (Date.now() - t0))
        if (get().schemeId !== id) return
        skipRecheck = true
        const node = r.accepted.node ?? item?.target ?? null
        const ts = Date.now()
        if (node) {
          window.setTimeout(() => {
            const a = { ...get().adapted }
            if (a[node] !== ts) return
            delete a[node]
            set({ adapted: a })
          }, 10_000)
        }
        set({
          scheme: r.scheme,
          sugg: r.suggestions,
          suggRead: null,
          adapted: node ? { ...get().adapted, [node]: ts } : get().adapted,
          notice: r.accepted.written.length ? `перестроено · записано: ${r.accepted.written.map((p) => p.split('/').slice(-2).join('/')).join(', ')}` : null,
        })
        void get().loadUsage()
      } catch (e) {
        fail(e)
        void get().loadSuggestions()
      } finally {
        set({ suggBusy: null })
      }
    },
    async rejectSuggestion(sug) {
      const id = get().schemeId
      const cur = get().sugg
      if (!id || !cur) return
      set({ sugg: { ...cur, items: cur.items.filter((x) => x.id !== sug) }, suggHover: get().suggHover === sug ? null : get().suggHover })
      try {
        const sugg = await flowApi.reject(id, sug)
        if (get().schemeId === id) set({ sugg })
      } catch (e) { fail(e) }
    },
    setSuggHover: (suggHover) => {
      if (get().suggHover === suggHover) return
      set({ suggHover })
      const item = suggHover ? get().sugg?.items.find((x) => x.id === suggHover) : null
      if (item?.target) get().focusNode(item.target)
    },
    setSuggRead: (suggRead) => set({ suggRead }),
    markSuggSeen: () => {
      const items = get().sugg?.items ?? []
      if (items.every((x) => get().suggSeen[x.id])) return
      const seen = { ...get().suggSeen }
      items.forEach((x) => { seen[x.id] = true })
      set({ suggSeen: seen })
    },
    focusNode: (id, always = false) => set({ focusRequest: { id, n: (get().focusRequest?.n ?? 0) + 1, always } }),
    async focusAgent(agentId) {
      if (get().mode !== 'live') get().setMode('live')
      if (!get().scheme) await get().loadSchemes()
      await get().loadUsage()
      const u = get().usage
      const nid = u?.rows[agentId]
        ?? (agentId.includes(':') ? u?.rows[agentId.split(':')[0]] : undefined)
      const n = nid ? get().scheme?.nodes.find((x) => x.id === nid) : undefined
      if (!n) {
        set({ notice: 'этого агента на схеме нет — расход по элементам ниже' })
        return
      }
      set({ sel: n.stack ? null : n.id, selEdge: null, tplOpen: null })
      get().focusNode(n.stack ?? n.id, true)
    },
    setRecording: (recording) => set({ recording }),
    async dictate(text) {
      const s = get().scheme
      if (!s) return
      set({ dictating: 'build' })
      try {
        await flowApi.dictate(s.id, text)
        await get().refresh()
      } catch (e) { fail(e) } finally { set({ dictating: null }) }
    },
    async resolveProposals(accept) {
      const s = get().scheme
      if (!s) return
      try {
        const scheme = await flowApi.proposals(s.id, accept)
        set({ scheme })
      } catch (e) { fail(e) }
    },
    setNotice: (notice) => set({ notice }),
  }

  /** Шаг прогона: оркестратор печатает «[flow] node=<id>» — подсвечиваем узел. */
  function watchRun(sessionId: string) {
    window.clearInterval(runPoll)
    let since = 0
    const started = Date.now()
    runPoll = window.setInterval(async () => {
      if (Date.now() - started > 30 * 60_000) { window.clearInterval(runPoll); return }
      try {
        const page = await api.log(sessionId, since)
        for (const line of page.lines) {
          since = Math.max(since, line.seq)
          const m = /\[flow\] node=([\w-]+)/.exec(line.text ?? '')
          if (m && get().scheme?.nodes.some((n) => n.id === m[1])) set({ runPrev: get().runStep, runStep: m[1] })
        }
      } catch {
        window.clearInterval(runPoll)
      }
    }, 3000)
  }
})


// «Ручные правки не блокируются» (README §4.5): после любой правки схемы —
// кроме перемещений и размеров — блок «Улучшения» на секунду показывает
// «перепроверяю…» и оставляет только актуальные рекомендации.
function signature(s: Scheme | null): string {
  if (!s) return ''
  return JSON.stringify([
    s.id,
    s.nodes.map((n) => [n.id, n.type, n.title, n.engine, n.skills, n.stack, n.proposed, n.status, n.meta]),
    s.edges.map((e) => [e.id, e.from, e.to]),
  ])
}
let lastSig = ''
let lastId: string | null = null
let recheckTimer = 0
useFlowStore.subscribe((st) => {
  const sig = signature(st.scheme)
  if (sig === lastSig) return
  const sameScheme = st.scheme?.id === lastId
  lastSig = sig
  lastId = st.scheme?.id ?? null
  if (!sameScheme || !st.sugg) return
  if (skipRecheck) { skipRecheck = false; return }
  window.clearTimeout(recheckTimer)
  recheckTimer = window.setTimeout(() => void useFlowStore.getState().loadSuggestions(true), 500)
})
